// Display names for alternate products shown in the unavailable state.
// These are only used for *other* products (never the current one). Alternate
// links use the truck-specific child URL from the configurator data; an alternate
// with no URL for the selected truck is unavailable and is not shown.
const PDP_PRODUCT_LABELS = {
  'diamondback-hd': 'Diamondback HD',
  'diamondback-se': 'Diamondback SE',
  'diamondback-switchback': 'Diamondback Switchback',
  'diamondback-270': 'Diamondback 270',
  'diamondback-180': 'Diamondback 180',
  'cross-bin-8-configurable': 'Cross Bin 8',
  'cross-bin-13-configurable': 'Cross Bin 13',
  'side-boxes-configurable': 'Side Boxes',
  'side-bin-8t-configurable': 'Side Bin 8T',
};

// Only these products show alternate links in the unavailable state.
const PDP_ALTERNATES = {
  'diamondback-switchback': ['diamondback-hd', 'diamondback-se'],
  'diamondback-270':        ['diamondback-hd', 'diamondback-se'],
  'diamondback-180':        ['diamondback-hd', 'diamondback-se'],
  'cross-bin-4t-configurable': ['cross-bin-8-configurable', 'side-boxes-configurable', 'side-bin-8t-configurable'],
  'side-bin-4t-configurable':  ['cross-bin-8-configurable', 'side-boxes-configurable', 'side-bin-8t-configurable', 'cross-bin-13-configurable'],
};

class ConfiguratorPdp extends (window.ConfiguratorBase || HTMLElement) {
  #collectionHandle = '';
  #baseChildUrl = null;
  #resolvedUrl = null;
  #resolvePromise = null;
  #productCache = {};

  getSource() { return 'pdp'; }

  cacheAdditionalRefs() {
    this.#collectionHandle = this.getAttribute('data-collection-handle') || '';
    this._formView = this.querySelector('[data-ref="formView"]');
    this._confirmedView = this.querySelector('[data-ref="confirmedView"]');
    this._unavailableView = this.querySelector('[data-ref="unavailableView"]');
    this._confirmedTruckValue = this.querySelector('[data-ref="confirmedTruckValue"]');
    this._continueLink = this.querySelector('[data-ref="continueLink"]');
    this._notMyTruckLink = this.querySelector('[data-ref="notMyTruckLink"]');
    this._unavailableTruckValue = this.querySelector('[data-ref="unavailableTruckValue"]');
    this._notMyTruckLink2 = this.querySelector('[data-ref="notMyTruckLink2"]');
    this._alternateLinksContainer = this.querySelector('[data-ref="alternateLinksContainer"]');
    this._alternateProducts = this.querySelector('[data-ref="alternateProducts"]');
  }

  bindAdditionalEvents() {
    this._continueLink?.addEventListener('click', async () => {
      if (!this.#baseChildUrl) return;
      // Wait for any in-flight resolution so the correct variant is baked in
      // before navigating. Falls back to the base child URL if it fails.
      if (this.#resolvePromise) {
        try { await this.#resolvePromise; } catch { /* ignore */ }
      }
      window.location.href = this.#resolvedUrl || this.#baseChildUrl;
    });
    this._notMyTruckLink?.addEventListener('click', (e) => {
      e.preventDefault();
      this._clearTruck();
    });
    this._notMyTruckLink2?.addEventListener('click', (e) => {
      e.preventDefault();
      this._clearTruck();
    });
    document.addEventListener('variantupdated', this.#handleVariantChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('variantupdated', this.#handleVariantChange);
  }

  async onSelectionComplete(selection, urls) {
    this._saveTruck(selection, urls);
    document.dispatchEvent(new CustomEvent('configurator:update', { detail: { selection, urls } }));
    const childUrl = this.#collectionHandle ? urls[this.#collectionHandle] : null;
    if (childUrl) {
      this.#showAvailableState(selection, childUrl);
    } else {
      this.#showUnavailableState(selection, urls);
    }
  }

  onReset() {
    this.#showFormState();
  }

  async onRestored(saved, urls) {
    const childUrl = this.#collectionHandle ? urls[this.#collectionHandle] : null;
    if (childUrl) {
      this.#showAvailableState(saved, childUrl);
    } else {
      this.#showUnavailableState(saved, urls);
    }
  }

  #showFormState() {
    this.#baseChildUrl = null;
    this.#resolvedUrl = null;
    this.#resolvePromise = null;
    this._formView?.removeAttribute('hidden');
    this._confirmedView?.setAttribute('hidden', '');
    this._unavailableView?.setAttribute('hidden', '');
  }

  #showAvailableState(selection, childUrl) {
    this.#baseChildUrl = childUrl;
    // Immediate fallback; the async resolver upgrades it to the exact variant.
    this.#resolvedUrl = childUrl;
    this.#resolvePromise = this.#resolveVariantUrl(childUrl)
      .then((url) => { this.#resolvedUrl = url; })
      .catch(() => { this.#resolvedUrl = childUrl; });
    const inbedSuffix = selection.inbed ? ` | TRACK: ${selection.inbed.toUpperCase()}` : '';
    const display = `${selection.year} | ${selection.make} | ${selection.model} | ${selection.bed} Bed${inbedSuffix}`;
    if (this._confirmedTruckValue) this._confirmedTruckValue.textContent = display;
    this._formView?.setAttribute('hidden', '');
    this._confirmedView?.removeAttribute('hidden');
    this._unavailableView?.setAttribute('hidden', '');
  }

  #showUnavailableState(selection, urls) {
    this.#baseChildUrl = null;
    this.#resolvedUrl = null;
    this.#resolvePromise = null;
    const inbedSuffix = selection.inbed ? ` | TRACK: ${selection.inbed.toUpperCase()}` : '';
    const display = `${selection.year} | ${selection.make} | ${selection.model} | ${selection.bed} Bed${inbedSuffix}`;
    if (this._unavailableTruckValue) this._unavailableTruckValue.textContent = display;
    this.#buildAlternateLinks(urls);
    const unavailableParams = { source: this.getSource(), ...selection, configurator_collection: this.#collectionHandle };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'configurator_unavailable', ...unavailableParams });
    if (window.Sentry) Sentry.captureMessage('configurator_unavailable', { level: 'warning', extra: unavailableParams });
    this._formView?.setAttribute('hidden', '');
    this._confirmedView?.setAttribute('hidden', '');
    this._unavailableView?.removeAttribute('hidden');
  }

  #buildAlternateLinks(urls) {
    const container = this._alternateLinksContainer;
    if (!container) return;
    const hideWrapper = () => this._alternateProducts?.setAttribute('hidden', '');
    const alternateHandles = PDP_ALTERNATES[this.#collectionHandle];
    if (!alternateHandles || !alternateHandles.length) {
      container.innerHTML = '';
      hideWrapper();
      return;
    }
    // Only show alternates that are actually available for this truck
    // (i.e. the configurator data has a child URL for them).
    const links = alternateHandles
      .filter((handle) => Boolean(urls[handle]))
      .map((handle) => ({
        label: PDP_PRODUCT_LABELS[handle] || handle,
        href: urls[handle],
      }));
    if (!links.length) {
      container.innerHTML = '';
      hideWrapper();
      return;
    }
    container.innerHTML = links
      .map((l) => `<a href="${l.href}" class="configurator-pdp__alt-link">Shop ${l.label}</a>`)
      .join('');
    if (links.length) {
      this._alternateProducts?.removeAttribute('hidden');
    } else {
      hideWrapper();
    }
  }

  // -- Variant resolution --
  //
  // Rewrites the child URL's ?variant= so it points at the child variant whose
  // options match the shopper's current selections on the parent product form.
  // Fully option-agnostic: it fetches the child product's native /products/<handle>.js,
  // starts from the incoming default variant's option tuple, and overrides each option
  // the shopper controls (matched by option NAME). No embedded data, no per-product
  // config. On any failure it returns the base URL unchanged (default variant).

  async #resolveVariantUrl(baseUrl) {
    if (!baseUrl) return baseUrl;

    let url;
    try {
      url = new URL(baseUrl, window.location.origin);
    } catch {
      return baseUrl;
    }

    const pathParts = url.pathname.split('/').filter(Boolean);
    const productsIndex = pathParts.indexOf('products');
    const handle = productsIndex === -1 ? null : pathParts[productsIndex + 1];
    const defaultVariantId = url.searchParams.get('variant');
    if (!handle || !defaultVariantId) return baseUrl;

    const product = await this.#getProduct(handle);
    if (!product || !Array.isArray(product.variants) || !Array.isArray(product.options)) return baseUrl;

    const selections = this.#getParentSelections();
    if (!Object.keys(selections).length) return baseUrl;

    const optionNames = product.options.map((o) => this.#normName(o && o.name ? o.name : o));
    const defaultVariant = product.variants.find((v) => String(v.id) === String(defaultVariantId));
    if (!defaultVariant || !Array.isArray(defaultVariant.options)) return baseUrl;

    // The set of values THIS child actually offers for each option position.
    const allowedValues = optionNames.map((_, i) =>
      new Set(product.variants.map((v) => this.#normVal(v.options[i])))
    );

    // Option positions the shopper controls AND whose chosen value the child offers
    // at all. (A value the child never offers, e.g. Vehicle Side "Passenger" on a
    // Fits-Both-only child, is not a controllable position — it stays at default.)
    const shopperPositions = optionNames
      .map((name, i) => ({ name, i }))
      .filter(({ name, i }) => name in selections && allowedValues[i].has(this.#normVal(selections[name])));
    const shopperSet = new Set(shopperPositions.map((p) => p.i));

    // Lock every non-shopper position (truck-fit dimensions, and any option whose
    // selected value the child can't satisfy) to the default variant's value, so we
    // never wander off the correct fit.
    let pool = product.variants.filter((v) =>
      Array.isArray(v.options)
      && v.options.length === optionNames.length
      && optionNames.every((_, i) =>
        shopperSet.has(i) || this.#normVal(v.options[i]) === this.#normVal(defaultVariant.options[i]))
    );
    if (!pool.length) pool = product.variants.slice();

    // Apply the shopper's selections in option order (priority), keeping each
    // constraint only while at least one variant still satisfies it. This carries
    // every selection the child CAN honor and drops only the ones it can't — e.g.
    // Cover "270" sticks, then Vehicle Side "Passenger" is dropped because no
    // "270 + Passenger" variant exists, landing on "270 / Fits Both".
    for (const { name, i } of shopperPositions) {
      const want = this.#normVal(selections[name]);
      const filtered = pool.filter((v) => this.#normVal(v.options[i]) === want);
      if (filtered.length) pool = filtered;
    }

    // Prefer an in-stock variant among the survivors.
    const match = pool.find((v) => v.available) || pool[0];
    if (!match) return baseUrl;

    url.searchParams.set('variant', String(match.id));

    // Cleats-specific: release the child's forced "must choose cleats" mask only
    // when the shopper actively picked a cleat on the parent (see product_form_child).
    if (this.#isCleatsActivelySelected()) {
      url.searchParams.set('allowCleatPreselect', 'true');
    }

    return `${url.pathname}${url.search}${url.hash}`;
  }

  // Fetch (and cache) the child product's option/variant data via Shopify's native
  // AJAX endpoint. Fired on truck selection (interaction), not on page load.
  async #getProduct(handle) {
    if (this.#productCache[handle] !== undefined) return this.#productCache[handle];
    try {
      const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
      const data = res.ok ? await res.json() : null;
      this.#productCache[handle] = data;
      return data;
    } catch {
      this.#productCache[handle] = null;
      return null;
    }
  }

  // { normalizedOptionName: value } for every option the shopper controls on the
  // parent form. Reads the button-option wrappers rendered by product_form_parent.
  #getParentSelections() {
    const out = {};
    document.querySelectorAll('.button-option-wrapper[data-option-name]').forEach((wrapper) => {
      const name = this.#normName(wrapper.getAttribute('data-option-name'));
      if (!name) return;
      const index = wrapper.getAttribute('data-option-index');
      let value = '';
      const select = index != null ? document.getElementById(`SingleOptionSelector-${index}`) : null;
      if (select) value = select.value;
      if (!value) {
        const btn = wrapper.querySelector('.button-option.selected');
        value = (btn?.getAttribute('data-val') || '').replace(/_/g, ' ');
      }
      if (value) out[name] = this.#normVal(value);
    });
    return out;
  }

  #isCleatsActivelySelected() {
    const wrapper = document.querySelector(
      '.button-option-wrapper[data-option-name="anchorcleats"], .button-option-wrapper[data-option-name="cleats"]'
    );
    if (!wrapper) return false;
    const selectedBtn = wrapper.querySelector('.button-option.selected');
    return selectedBtn ? !selectedBtn.classList.contains('masked-selection') : false;
  }

  // Matches the parent's `option.name | handleize | remove:"-"` so parent
  // "anchorcleats" lines up with a child option named "Anchor Cleats".
  #normName(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  #normVal(value) {
    return String(value == null ? '' : value).trim().replace(/\s+/g, ' ').toLowerCase();
  }

  // -- External sync --

  #handleVariantChange = () => {
    if (!this.#baseChildUrl || this._confirmedView?.hasAttribute('hidden')) return;
    // Shopper changed an option after selecting the truck: re-resolve.
    this.#resolvePromise = this.#resolveVariantUrl(this.#baseChildUrl)
      .then((url) => { this.#resolvedUrl = url; })
      .catch(() => { this.#resolvedUrl = this.#baseChildUrl; });
  };
}

if (!customElements.get('configurator-pdp-component')) {
  customElements.define('configurator-pdp-component', ConfiguratorPdp);
}
